import { type NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import React, { useMemo } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import {
  getIsActiveNode,
  getIsDragging,
  getIsHighlighted,
  getNodeStyle,
  getIsMultipleNodesByParent,
  getIsDestination,
  getIsActiveAltKey,
  getSelectedNodesByParent,
  getIsShowGrid,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import type { IBlockMode } from 'services/Store/Project/types'

import { AbsoluteControls, AbsoluteChildrenControls } from '../DisplayProvider/Absolute'
import { FlexControls, FlexChildrenControls } from '../DisplayProvider/Flex'
import { GridControls, GridChildrenControls } from '../DisplayProvider/Grid'
import { isAbsolute, isFlex, isGrid, isRoot as checkIsRoot } from '../guards'
import * as s from './Controls.module.scss'
import NodeV2ElementControls from './NodeV2ElementControls'
import NodeV2GroupSelection from './NodeV2GroupSelection'
import { NodeV2MultiLabel, NodeV2SingleLabel, NodeV2HighlightLabel } from './NodeV2Label'
import NodeV2Repeatable from './NodeV2Repeatable'
import NodeV2Selection from './NodeV2Selection'
import NodeV2SvgInfo from './NodeV2SvgInfo'

// import { selectRealtimeUsers } from 'services/Store/Users/selectors'
// import { useAppSelector } from 'services/Store/hooks'
// import RealtimeUsers from './RealtimeUsers'

interface ICellsManagementControlsProps {
  mode: IBlockMode
  block: Block
  parent: NodeV2 | null
  node: NodeV2
  element: EditorElement | null
  nodeRef: React.RefObject<HTMLDivElement>
}

const NodeV2Controls: React.FC<ICellsManagementControlsProps> = ({
  node,
  block,
  parent,
  mode,
  element,
  nodeRef,
}) => {
  const isPro = mode.editorMode === AppMode.pro
  const selected = useProjectContext(getSelectedNodesByParent, block.uuid, node.id)
  const isActive = useProjectContext(getIsActiveNode, node.id)
  const isHighlighted = useProjectContext(getIsHighlighted, node.id)
  const isMultiple = useProjectContext(getIsMultipleNodesByParent, block.uuid, parent?.id || null)
  const isDragging = useProjectContext(getIsDragging)
  const isDestination = useProjectContext(getIsDestination, node.id)
  const isAlt = useProjectContext(getIsActiveAltKey)
  const isShowGrid = useProjectContext(getIsShowGrid, block.uuid, node.id)
  const isRoot = checkIsRoot(block, node)
  const style = useProjectContext(getNodeStyle, block.uuid, node.id)
  const parentStyle = useProjectContext(getNodeStyle, block.uuid, parent?.id || null)

  const isEveryAbsolute = useMemo(() => {
    return selected.every((node) => {
      const style = mergeNodeStyle(node?.style || {}, mode.deviceMode)
      return style.flexFlow === 'absolute'
    })
  }, [selected, mode.deviceMode])

  return (
    <>
      <NodeV2Selection styles={style}>
        {/* {Boolean(users?.length) && <RealtimeUsers users={users} />} */}
        {!isDragging && (isActive || isHighlighted) && (
          <AbsolutePortal name='selection' recalculatingKey={block}>
            <div className={cn(s.border, s[parentStyle?.display || style?.display || 'element'])} />
          </AbsolutePortal>
        )}
        {!isDragging && !isAlt && isHighlighted && (!isActive || isMultiple) && (
          <NodeV2HighlightLabel block={block} mode={mode} node={node} parent={parent} />
        )}
        {!isDragging && !isAlt && isActive && !isMultiple && (
          <NodeV2SingleLabel block={block} mode={mode} node={node} parent={parent} />
        )}
        {!isMultiple && !isAlt && isActive && !isDragging && node.repeatable && (
          <NodeV2Repeatable block={block} mode={mode} node={node} styles={style} />
        )}
        {element && !isAlt && !isMultiple && !isDragging && isActive && (
          <NodeV2ElementControls block={block} element={element} mode={mode} node={node} />
        )}
        {isPro && (
          <NodeV2SvgInfo
            block={block}
            mode={mode}
            node={node}
            parent={parent}
            selected={selected}
          />
        )}
      </NodeV2Selection>

      {!isDragging && selected.length > 0 && (
        <NodeV2GroupSelection
          block={block}
          mode={mode}
          node={node}
          nodeRef={nodeRef}
          selected={selected}
        >
          {selected.length > 1 && !isAlt && (
            <NodeV2MultiLabel block={block} mode={mode} node={node} selected={selected} />
          )}
          {isGrid(style) && !isAlt && (
            <GridChildrenControls block={block} mode={mode} node={node} selected={selected} />
          )}
          {isFlex(style) && !isAlt && (
            <FlexChildrenControls block={block} mode={mode} node={node} selected={selected} />
          )}
          {(isAbsolute(style) || (isFlex(style) && isEveryAbsolute)) && !isAlt && (
            <AbsoluteChildrenControls block={block} mode={mode} node={node} selected={selected} />
          )}
        </NodeV2GroupSelection>
      )}

      {isFlex(style) && !isAlt && isActive && (
        <FlexControls block={block} mode={mode} node={node} />
      )}
      {isGrid(style) && !isAlt && (isActive || isDestination || isShowGrid || isRoot) && (
        <GridControls block={block} mode={mode} node={node} />
      )}
      {isAbsolute(style) && !isAlt && (isActive || isDestination) && (
        <AbsoluteControls block={block} mode={mode} node={node} />
      )}
    </>
  )
}

export default React.memo(NodeV2Controls)
